/**
 * **The session's deck** — what `/zadania` is working through, and the one rule that makes the
 * index readable: *a handled item stays where it is.*
 *
 * The Pulpit's cards remove an answered row and close the gap behind it, which is right for a
 * five-row card a trainer glances at. It is wrong here. This screen is a list of fourteen with a
 * position chip reading "4 z 14" and a progress meter: a list that shrinks under the trainer has no
 * denominator, and every answer moves the rows below the one they just pressed. So an answered card
 * is struck through in place, the total never changes during a sitting, and "3 z 14" means what it
 * says.
 *
 * Pure: no React, no DOM, no query hook, no i18next. Every operation returns a NEW deck; nothing
 * here mutates.
 *
 * ── The deck is seeded in the SERVER's order and never re-sorted ─────────────────────────────
 * The backend ranks by severity and then recency (R8, `RankAndCap`). Sorting here would be a second
 * implementation of a rule that already has one, and the two would eventually disagree about a tie.
 *
 * ── An item that vanishes without a local answer was resolved somewhere else (R5, R15) ───────
 * The queue is implicit: writing the note, marking the survey reviewed, watching the clip, or the
 * client simply finishing the workout, all make the item stop being returned. A fresh read that no
 * longer carries a card the trainer never answered is therefore evidence the work was DONE, not
 * evidence it was lost — so the card stays in place marked done, and the count still adds up. The
 * one thing that must not happen is the card silently disappearing and the denominator changing
 * under the trainer mid-sitting.
 *
 * ── A reached goal is not in the deck; a personal record is (R14, R24) ───────────────────────
 * Which entries are tasks at all is `features/attention-queue`'s `taskSectionOf`, the one decision
 * the Pulpit and the client's Przegląd share: Triage and Feedback, plus the personal record, which
 * R24 makes a task like any other and which is worked with the Triage half — it counts in „Zadania"
 * and a bulk „gotowe" takes it. `GoalAchieved` stays out; it reaches the trainer through the
 * roster-wins feed at the foot of the Pulpit.
 */

import type { AttentionReason } from '~/entities/client';
import {
  taskSectionOf,
  type AttentionEntry,
  type AttentionItem,
  type TaskSection,
} from '~/features/attention-queue';

/** Which half of the deck is on screen. `all` is both. */
export type DeckScope = 'all' | 'Triage' | 'Feedback';

/** Every scope, in the order the switch draws them. */
export const DECK_SCOPES: readonly DeckScope[] = ['all', 'Triage', 'Feedback'];

/**
 * What the trainer answered about a card, and how.
 *
 * `done` and `snoozed` are QUEUE RESPONSES: private to the responder (R4), held five seconds before
 * they are written, and recallable with `Z` for as long as they are held.
 *
 * `resolved` is not a response at all. Writing the session note, marking the survey reviewed and
 * watching the clip each make the item stop being true on the SERVER (R15, `intake-survey` R28,
 * `exercise-clip-timeline` R9/R18) — what the requirements call implicit resolution. Nothing is
 * posted to `/attention-queue/responses` for one, there is nothing held, and **it cannot be taken
 * back**: the server already has the note. Drawing it as `done` and holding it for five seconds
 * offered the trainer an undo that would have left the item resolved and the card open.
 *
 * `null` means the card is still waiting.
 */
export type HandledKind = 'done' | 'snoozed' | 'resolved';

/** The two acts a bulk menu offers. Both are responses; there is no bulk implicit resolution. */
export type BulkAct = 'done' | 'snoozed';

/** One card in the deck. */
export interface DeckCard {
  readonly item: AttentionItem;
  /** The half of the deck the card is worked in — a personal record stands with Triage (R24). */
  readonly category: TaskSection;
  readonly handled: HandledKind | null;
}

export interface TaskDeck {
  /** Every card, in the server's order, answered ones included and in place. */
  readonly cards: readonly DeckCard[];
  readonly scope: DeckScope;
  /** The card the desk is showing, or `null` when the deck is empty. */
  readonly selectedKey: string | null;
}

/** How much of the deck has an answer. The index's meter and its "N z M". */
export interface DeckProgress {
  readonly done: number;
  readonly total: number;
}

/** How many cards each scope holds. Answered ones count — the switch states the deck, not the work left. */
export interface DeckCounts {
  readonly all: number;
  readonly Triage: number;
  readonly Feedback: number;
}

/** What the end state tallies. */
export interface DeckTally {
  readonly done: number;
  readonly snoozed: number;
}

function deckCardsOf(entries: readonly AttentionEntry[]): DeckCard[] {
  const cards: DeckCard[] = [];
  for (const entry of entries) {
    const category = taskSectionOf(entry);
    if (category !== null) cards.push({ item: entry.item, category, handled: null });
  }
  return cards;
}

/** A deck with a selection on it, or with `null` when there is nothing to select. */
function withSelection(cards: readonly DeckCard[], scope: DeckScope, wanted: string | null): TaskDeck {
  const visible = cards.filter((card) => matchesScope(card, scope));
  const stillThere = wanted !== null && visible.some((card) => card.item.key === wanted);
  if (stillThere) return { cards, scope, selectedKey: wanted };
  const firstOpen = visible.find((card) => card.handled === null);
  return { cards, scope, selectedKey: (firstOpen ?? visible[0])?.item.key ?? null };
}

function matchesScope(card: DeckCard, scope: DeckScope): boolean {
  return scope === 'all' || card.category === scope;
}

/**
 * The deck a fresh queue read makes, with the first open card of `scope` selected. The scope is the
 * address's (`?scope=`), so a link from the Pulpit's summary line opens on the half it counted.
 */
export function seedDeck(entries: readonly AttentionEntry[], scope: DeckScope = 'all'): TaskDeck {
  return withSelection(deckCardsOf(entries), scope, null);
}

/**
 * `/zadania`'s search: `?scope=Triage` or `?scope=Feedback`, and nothing for both halves.
 *
 * Added 3 November 2026 for the Pulpit's summary line („wczoraj 2 opuszczone treningi" opens the
 * Triage half, „6 do przejrzenia" the Feedback half). Additive: no parameter is the screen it was.
 * An unrecognised value is DROPPED rather than defaulted into the URL, and the default is omitted,
 * for the reasons `pages/reports`' `validateReportsSearch` gives.
 */
export interface TasksSearch {
  readonly scope?: Exclude<DeckScope, 'all'>;
}

export function validateTasksSearch(raw: Record<string, unknown>): TasksSearch {
  return raw.scope === 'Triage' || raw.scope === 'Feedback' ? { scope: raw.scope } : {};
}

/** Which half an address asks for. Anything unrecognised is both. */
export function deckScopeOf(search: TasksSearch): DeckScope {
  return search.scope === 'Triage' || search.scope === 'Feedback' ? search.scope : 'all';
}

/**
 * Fold a fresh read into a deck the trainer has been working.
 *
 * Three rules, and each is the answer to a question a naive re-seed gets wrong:
 *
 * · **A card still on the server keeps its local answer and takes the server's item.** The payload
 *   may have moved on — a session's chip counts, a plan's end date — and the trainer's own answer is
 *   not on the wire until the write lands.
 * · **A card gone WITHOUT a local answer counts as resolved** (R5). It stays in place, marked done.
 * · **A new arrival is APPENDED**, never merged into the server's order. Re-sorting mid-sitting
 *   would move every row under the trainer's cursor to make room for something that just happened.
 */
export function reconcileDeck(
  deck: TaskDeck,
  entries: readonly AttentionEntry[],
  mutedReasons: readonly AttentionReason[] = [],
): TaskDeck {
  const fresh = new Map<string, DeckCard>();
  for (const card of deckCardsOf(entries)) fresh.set(card.item.key, card);

  // A kind the trainer has just muted LEAVES, answered or not (R17: a muted reason is absent from
  // the queue itself, on every surface). It is not "resolved somewhere else": striking it through
  // would keep on the desk exactly what the trainer asked not to see.
  const muted = new Set(mutedReasons);
  const kept = deck.cards.filter((card) => !muted.has(card.item.reason));

  const cards: DeckCard[] = kept.map((card) => {
    const server = fresh.get(card.item.key);
    fresh.delete(card.item.key);
    if (server !== undefined) return { ...server, handled: card.handled };
    // Gone from the server. An answered card keeps its own answer; an unanswered one was resolved
    // somewhere else and says so.
    return card.handled === null ? { ...card, handled: 'done' } : card;
  });

  for (const arrival of fresh.values()) cards.push(arrival);
  return withSelection(cards, deck.scope, deck.selectedKey);
}

/** Record an answer against one or more cards, leaving every one of them where it stands. */
export function markHandled(
  deck: TaskDeck,
  keys: readonly string[],
  kind: HandledKind,
): TaskDeck {
  const marked = new Set(keys);
  const cards = deck.cards.map((card) =>
    marked.has(card.item.key) ? { ...card, handled: kind } : card,
  );
  return withSelection(cards, deck.scope, nextOpenAfter({ ...deck, cards }, deck.selectedKey));
}

/**
 * Record an implicit resolution: the SERVER already has it, so there is nothing to respond and
 * nothing to take back.
 *
 * A saved session note, a survey marked reviewed, a watched clip. The card is struck through in
 * place like any answered one — what differs is that no queue response is written and
 * `unmarkHandled` will not reopen it.
 *
 * ── `keepOnDesk`, and it is the clip's ───────────────────────────────────────────────────────
 * The note and the reviewed survey are FINISHED with, so the desk moves on to the next open card,
 * which is what every other answer does. A clip is not: the trainer is watching it, and `seen` is
 * posted the moment it starts. Advancing would take the film off the desk two seconds into it —
 * the same defect as invalidating the queue under a playing `<video>`, which
 * `exercise-clip-timeline` R9/R18 is the reason this screen deliberately does not do.
 */
export function resolveImplicitly(
  deck: TaskDeck,
  key: string,
  { keepOnDesk = false }: { readonly keepOnDesk?: boolean } = {},
): TaskDeck {
  const marked = markHandled(deck, [key], 'resolved');
  return keepOnDesk ? withSelection(marked.cards, marked.scope, deck.selectedKey) : marked;
}

/**
 * Take an answer back — an undo, or a write the server refused.
 *
 * The cards return to open IN THEIR OWN PLACES, because they never left them, and the FIRST of them
 * takes the selection: an undo is a request to look at that card again.
 *
 * **An implicitly resolved card is never among them.** There is no response to withdraw and the
 * note is already saved, so reopening it would put a card back on a queue the server has already
 * stopped returning.
 */
export function unmarkHandled(deck: TaskDeck, keys: readonly string[]): TaskDeck {
  const marked = new Set(
    keys.filter(
      (key) => deck.cards.find((card) => card.item.key === key)?.handled !== 'resolved',
    ),
  );
  const cards = deck.cards.map((card) =>
    marked.has(card.item.key) ? { ...card, handled: null } : card,
  );
  const first = cards.find((card) => marked.has(card.item.key));
  // The scope may be hiding the returned card — `Feedback` while a Triage card comes back. Widening
  // to `all` is the only way the trainer can see what they just recovered.
  const scope = first !== undefined && !matchesScope(first, deck.scope) ? 'all' : deck.scope;
  return withSelection(cards, scope, first?.item.key ?? deck.selectedKey);
}

export function setScope(deck: TaskDeck, scope: DeckScope): TaskDeck {
  return withSelection(deck.cards, scope, deck.selectedKey);
}

export function selectCard(deck: TaskDeck, key: string): TaskDeck {
  return withSelection(deck.cards, deck.scope, key);
}

/** One step up or down the visible list. Clamped at both ends — the deck does not wrap on arrows. */
export function stepSelection(deck: TaskDeck, delta: number): TaskDeck {
  const visible = cardsInScope(deck);
  const at = visible.findIndex((card) => card.item.key === deck.selectedKey);
  if (at === -1) return deck;
  const next = visible[Math.min(visible.length - 1, Math.max(0, at + delta))];
  return next === undefined ? deck : withSelection(deck.cards, deck.scope, next.item.key);
}

/**
 * The next open card after `key`, wrapping once through the visible list, or `null` when none is
 * left.
 *
 * It WRAPS, unlike the arrows: answering the last open card of a sitting should land on the first
 * one still waiting rather than stopping at the end of the list.
 */
export function nextOpenAfter(deck: TaskDeck, key: string | null): string | null {
  const visible = cardsInScope(deck);
  const at = visible.findIndex((card) => card.item.key === key);
  const ordered = at === -1 ? visible : [...visible.slice(at + 1), ...visible.slice(0, at + 1)];
  return ordered.find((card) => card.handled === null)?.item.key ?? null;
}

export function cardsInScope(deck: TaskDeck): readonly DeckCard[] {
  return deck.cards.filter((card) => matchesScope(card, deck.scope));
}

export function selectedCard(deck: TaskDeck): DeckCard | null {
  return deck.cards.find((card) => card.item.key === deck.selectedKey) ?? null;
}

/** Where the selected card stands in the WHOLE deck, one-based, for the position chip. */
export function selectedPosition(deck: TaskDeck): number {
  return deck.cards.findIndex((card) => card.item.key === deck.selectedKey) + 1;
}

export function deckCounts(deck: TaskDeck): DeckCounts {
  let triage = 0;
  for (const card of deck.cards) if (card.category === 'Triage') triage += 1;
  return { all: deck.cards.length, Triage: triage, Feedback: deck.cards.length - triage };
}

export function deckProgress(deck: TaskDeck): DeckProgress {
  let done = 0;
  for (const card of deck.cards) if (card.handled !== null) done += 1;
  return { done, total: deck.cards.length };
}

export function deckTally(deck: TaskDeck): DeckTally {
  let done = 0;
  let snoozed = 0;
  for (const card of deck.cards) {
    // An implicit resolution counts with the done: the trainer dealt with it, and the tally is
    // about the sitting rather than about which endpoint recorded it.
    if (card.handled === 'done' || card.handled === 'resolved') done += 1;
    else if (card.handled === 'snoozed') snoozed += 1;
  }
  return { done, snoozed };
}

/** Every card still waiting, anywhere in the deck — not only in the scope on screen. */
export function openCards(deck: TaskDeck): readonly DeckCard[] {
  return deck.cards.filter((card) => card.handled === null);
}

/** Nothing is waiting. The end state's gate. */
export function deckIsCleared(deck: TaskDeck): boolean {
  return deck.cards.length > 0 && openCards(deck).length === 0;
}

/**
 * Which cards a bulk act would touch.
 *
 * **Bulk "gotowe" covers TRIAGE only.** A feedback item is a client waiting for the trainer's word,
 * and clearing a column of them in one press says "handled" about work nobody did. Deferring them
 * is honest, so "odłóż wszystko" takes everything. The menu says as much in a sentence rather than
 * leaving the asymmetry to be discovered.
 */
export function bulkTargets(deck: TaskDeck, act: BulkAct): readonly string[] {
  return openCards(deck)
    .filter((card) => act === 'snoozed' || card.category === 'Triage')
    .map((card) => card.item.key);
}
